import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function HvorMegetMaaManTraekke() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Hvor meget må man trække", path: "/hvor-meget-maa-man-traekke" }
  ];
  const tocItems = [
    { id: "to-graenser", title: "To forskellige grænser: Kørekort vs. bil" },
    { id: "uden-trailerkort", title: "Hvor meget må du trække uden trailerkort?" },
    { id: "med-trailerkort", title: "Hvor meget må du trække med trailerkort (BE)?" },
    { id: "registreringsattest", title: "Sådan læser du bilens registreringsattest" },
    { id: "beregningseksempler", title: "Typiske beregningseksempler" }
  ];

  const faq = [
    {
      q: "Må jeg hænge en tom 2.000 kg trailer på min bil uden trailerkort?",
      a: "Kun hvis bilens tilladte totalvægt plus trailerens tilladte totalvægt på papiret ikke overstiger 3.500 kg. Selvom traileren er helt tom og vejer 400 kg, vurderes dit kørekort altid ud fra trailerens registrerede totalvægt (2.000 kg). Har bilen en totalvægt over 1.500 kg, kræver det BE-kørekort."
    },
    {
      q: "Hvor ser jeg, hvad min bil maksimalt må trække?",
      a: "I feltet for 'Teknisk tilladt totalvægt af påhængskøretøj' på bilens registreringsattest. Der er to felter: ét med bremser og ét uden bremser. Den faktiske vægt af trailer plus last må aldrig overstige disse tal."
    },
    {
      q: "Må trailerens tilladte totalvægt være højere end bilens trækevne?",
      a: "Ja, det må den gerne juridisk set. Bilen må trække traileren, så længe trailerens faktiske totalvægt (egenvægt + last) ikke overskrider bilens tilladte koblingsvægt, og dit kørekort dækker vogntogets tilladte totalvægt."
    },
    {
      q: "Hvad sker der, hvis jeg kører med for meget vægt?",
      a: "Kørsel med overlæs straffes med bøde pr. procent overskridelse. Kører du med et vogntog, der overskrider dit kørekort, betragtes det juridisk som kørsel uden gyldigt kørekort, hvilket udløser bøde og i grove tilfælde frakendelse."
    }
  ];

  const sources = [
    { title: "Kørekortbekendtgørelsen (BEK nr. 1243 af 28/08/2020)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2020/1243" },
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Færdselsstyrelsen: Kørekort til påhængskøretøj til bil", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/udvidelse-af-koerekort/koerekort-til-paahaengskoeretoej-til-bil" }
  ];

  return (
    <>
      <SEO 
        title="Hvor meget må man trække med og uden trailerkørekort?" 
        description="Lær reglerne for hvor meget du må trække med B-kørekort og BE-kørekort. Se forskellen på bilens trækevne og kørekortets vægtgrænser." 
        pathname="/hvor-meget-maa-man-traekke"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Hvor meget må man trække med og uden trailerkort?
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Hvor meget du må trække, bestemmes altid af det laveste tal af to uafhængige regelsæt: Hvad dit kørekort tillader ifølge kørekortbekendtgørelsen, og hvad din bil må trække ifølge sin registreringsattest.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Uden trailerkort (kategori B) må du altid trække en trailer på op til 750 kg tilladt totalvægt. Er traileren tungere end 750 kg, må bilens og trailerens tilladte totalvægte tilsammen højst være 3.500 kg. Med et trailerkort (kategori BE) må du trække en trailer på op til 3.500 kg, så længe bilens registreringsattest tillader det.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Den gyldne tommelfingerregel">
          <p className="m-0">
            <strong>Kørekortet</strong> ser på papiret (tilladt totalvægt). <strong>Bilen</strong> ser på vægten (faktisk vægt af trailer + last). Du skal overholde begge dele samtidigt for at køre lovligt.
          </p>
        </Callout>

        <h2 id="to-graenser" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          To forskellige grænser: Kørekort vs. bil
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          For at køre lovligt med en trailer skal to helt adskilte betingelser altid være opfyldt på samme tid:
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-4">
          <div>
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              1. Kørekortets begrænsning (Juridisk)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Vurderes ud fra <strong>tilladt totalvægt</strong> på bilens og trailerens registreringsattester. Det er uden betydning, om traileren er læsset med beton eller er helt tom.
            </p>
          </div>
          <div className="border-t border-[#E4E1D8] pt-3">
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              2. Bilens begrænsning (Teknisk)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Vurderes ud fra <strong>faktisk totalvægt</strong>. Trailerens egenvægt plus lastens vægt må aldrig overskride den koblingsvægt, bilen er godkendt til (med eller uden bremser).
            </p>
          </div>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om begreberne i vores grundige guide til <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link>.
        </p>

        <h2 id="uden-trailerkort" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvor meget må du trække uden trailerkort? (Kategori B)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Med et almindeligt B-kørekort må du føre følgende:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>En bil med tilladt totalvægt op til 3.500 kg og en trailer med tilladt totalvægt på <strong>højst 750 kg</strong> (vogntog op til 4.250 kg).</li>
          <li>En bil og en trailer på <strong>over 750 kg</strong>, hvis summen af bilens tilladte totalvægt og trailerens tilladte totalvægt ikke overstiger <strong>3.500 kg</strong>.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Har du en bil med en tilladt totalvægt på 2.100 kg, må du altså højst hænge en trailer med en tilladt totalvægt på 1.400 kg efter den uden trailerkort (2.100 + 1.400 = 3.500 kg), forudsat at bilen må trække 1.400 kg.
        </p>

        <h2 id="med-trailerkort" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvor meget må du trække med trailerkort? (Kategori BE)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du erhverver et kategori BE-kørekort, ophæves 3.500 kg-loftet for vogntoget. Med BE må du:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Føre en bil med tilladt totalvægt på op til 3.500 kg.</li>
          <li>Trække en trailer med tilladt totalvægt på op til 3.500 kg.</li>
          <li>Køre med en samlet vogntogsvægt på helt op til 7.000 kg.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Med et BE-kørekort er det i praksis næsten altid bilens tekniske godkendelse, der sætter grænsen for, hvor tung en trailer du må køre med. Læs alt om prøven og priser i vores artikel om <Link to="/be-koerekort" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">stort trailerkørekort (BE)</Link>.
        </p>

        <h2 id="registreringsattest" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan læser du bilens registreringsattest
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          På din bils registreringsattest (del 1) skal du aflæse følgende felter:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Felt</th>
                <th className="py-2.5 pr-4 font-semibold">Betegnelse</th>
                <th className="py-2.5 font-semibold">Betydning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">F.1</td>
                <td className="py-3 pr-4">Teknisk tilladt totalvægt</td>
                <td className="py-3 text-[#5C636B]">Bilens maksimale vægt med passagerer og bagage</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">O.1</td>
                <td className="py-3 pr-4">Største påhængsvognsvægt m/bremser</td>
                <td className="py-3 text-[#5C636B]">Maksimal faktisk vægt bilen må trække med bremser</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">O.2</td>
                <td className="py-3 pr-4">Største påhængsvognsvægt u/bremser</td>
                <td className="py-3 text-[#5C636B]">Maksimal faktisk vægt bilen må trække uden bremser</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="beregningseksempler" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Typiske beregningseksempler
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Nedenfor ses typiske kombinationer af bil og trailer og det juridiske udfald:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Bilens data</th>
                <th className="py-2.5 pr-4 font-semibold">Trailerens data</th>
                <th className="py-2.5 pr-4 font-semibold">Faktisk last</th>
                <th className="py-2.5 font-semibold">Lovlighed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4">Total: 2.100 kg<br/><span className="text-[13px] text-[#5C636B]">Må trække 1.500 kg</span></td>
                <td className="py-3 pr-4">Total: 1.300 kg<br/><span className="text-[13px] text-[#5C636B]">Egenvægt 300 kg</span></td>
                <td className="py-3 pr-4">800 kg gods<br/><span className="text-[13px] text-[#5C636B]">(1.100 kg i alt)</span></td>
                <td className="py-3 font-semibold text-[#2E3A46]">Lovligt med B-kørekort<br/><span className="text-[13px] font-normal text-[#5C636B]">Sum: 3.400 kg</span></td>
              </tr>
              <tr>
                <td className="py-3 pr-4">Total: 2.300 kg<br/><span className="text-[13px] text-[#5C636B]">Må trække 1.800 kg</span></td>
                <td className="py-3 pr-4">Total: 1.500 kg<br/><span className="text-[13px] text-[#5C636B]">Egenvægt 400 kg</span></td>
                <td className="py-3 pr-4">Tom trailer<br/><span className="text-[13px] text-[#5C636B]">(400 kg i alt)</span></td>
                <td className="py-3 font-semibold text-[#B58200]">Ulovligt på B-kort<br/><span className="text-[13px] font-normal text-[#5C636B]">Kræver B+ el. BE (sum 3.800 kg)</span></td>
              </tr>
              <tr>
                <td className="py-3 pr-4">Total: 1.900 kg<br/><span className="text-[13px] text-[#5C636B]">Må trække 1.200 kg</span></td>
                <td className="py-3 pr-4">Total: 1.500 kg<br/><span className="text-[13px] text-[#5C636B]">Egenvægt 350 kg</span></td>
                <td className="py-3 pr-4">700 kg gods<br/><span className="text-[13px] text-[#5C636B]">(1.050 kg i alt)</span></td>
                <td className="py-3 font-semibold text-[#2E3A46]">Lovligt med B-kørekort<br/><span className="text-[13px] font-normal text-[#5C636B]">Sum 3.400 kg, bil må trække 1.050 kg</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også mere om <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerens totalvægt og nyttelast</Link>, eller tjek reglerne for <Link to="/lille-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">lille trailer (350–500 kg)</Link> og <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
